/**
 * Hyrrim shared configurator state.
 *
 * Single source of truth for the sauna-configurator ↔ heater-PDP handoff
 * defined in `plans/heater-pdp-redesign/conventions.md`
 * ("Configurator-state architecture"). Writes here are durable across page
 * navigations (localStorage) and broadcast to other tabs (BroadcastChannel
 * + cross-tab storage events as a fallback).
 *
 * Public API at `window.__hyrrimConfiguratorState`:
 *   get(): ConfiguratorState | null
 *   setSauna(handle, variantLabel)
 *   setOven(handle, opts)         // opts: { variantId }
 *   addAccessory(handle, opts)    // opts: { variantId, parentHeaterHandle }
 *   removeAccessory(handle)
 *   clear()
 *   subscribe(callback)           // callback(state, change); returns unsubscribe
 *
 * Defensive read pattern (consumers): window.__hyrrimConfiguratorState?.get?.()
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'hyrrim:configurator:state:v1';
  var CHANNEL_NAME = 'hyrrim:configurator:state-updated';
  var EXPIRY_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
  var VERSION = 1;

  var channel = null;
  try {
    if (typeof BroadcastChannel === 'function') {
      channel = new BroadcastChannel(CHANNEL_NAME);
    }
  } catch (_e) {
    channel = null;
  }

  var subscribers = [];

  function emptyState() {
    return {
      version: VERSION,
      sauna_handle: null,
      sauna_variant_label: null,
      selected_oven_handle: null,
      selected_oven_variant_id: null,
      oven_accessories: [],
      updated_at: Date.now()
    };
  }

  function read() {
    var raw;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      return null;
    }
    if (!raw) return null;

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_e) {
      removeKey();
      return null;
    }

    if (!parsed || parsed.version !== VERSION) {
      removeKey();
      return null;
    }

    if (typeof parsed.updated_at !== 'number' || Date.now() - parsed.updated_at > EXPIRY_MS) {
      removeKey();
      return null;
    }

    // Defensive shape normalization in case external code wrote a partial.
    parsed.sauna_handle = parsed.sauna_handle || null;
    parsed.sauna_variant_label = parsed.sauna_variant_label || null;
    parsed.selected_oven_handle = parsed.selected_oven_handle || null;
    parsed.selected_oven_variant_id =
      typeof parsed.selected_oven_variant_id === 'number'
        ? parsed.selected_oven_variant_id
        : null;
    parsed.oven_accessories = Array.isArray(parsed.oven_accessories)
      ? parsed.oven_accessories
      : [];

    return parsed;
  }

  function removeKey() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      /* ignore */
    }
  }

  function write(state, change) {
    state.updated_at = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // Private-browsing Safari throws on setItem; keep going so the in-page
      // state still works for the duration of this tab session.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[configurator-state] localStorage write failed:', e);
      }
    }
    notifyLocal(state, change);
    broadcast(change);
  }

  function notifyLocal(state, change) {
    for (var i = 0; i < subscribers.length; i++) {
      try {
        subscribers[i](state, change);
      } catch (e) {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[configurator-state] subscriber threw:', e);
        }
      }
    }
  }

  function broadcast(change) {
    if (!channel) return;
    try {
      channel.postMessage({
        updated_at: Date.now(),
        source: 'local',
        change: change || null
      });
    } catch (_e) {
      /* ignore */
    }
  }

  // Mutators ---------------------------------------------------------------

  function setSauna(handle, variantLabel) {
    var state = read() || emptyState();
    state.sauna_handle = handle || null;
    state.sauna_variant_label = variantLabel || null;
    write(state, { kind: 'sauna-changed', payload: { handle: state.sauna_handle, variantLabel: state.sauna_variant_label } });
  }

  function setOven(handle, opts) {
    var state = read() || emptyState();
    var variantId = opts && typeof opts.variantId === 'number' ? opts.variantId : null;
    var previousHandle = state.selected_oven_handle;

    state.selected_oven_handle = handle || null;
    state.selected_oven_variant_id = variantId;

    // Heater-change rule: clear accessories when the heater handle differs
    // from the previously-selected one. (Compatibility is heater-specific.)
    if (previousHandle && handle && previousHandle !== handle) {
      state.oven_accessories = [];
    }
    // Also clear if the oven is being deselected entirely.
    if (!handle) {
      state.oven_accessories = [];
    }

    write(state, { kind: 'oven-changed', payload: { handle: state.selected_oven_handle, variantId: state.selected_oven_variant_id } });
  }

  function addAccessory(handle, opts) {
    if (!handle) return;
    var state = read() || emptyState();
    var variantId = opts && typeof opts.variantId === 'number' ? opts.variantId : null;
    var parentHeaterHandle = opts && opts.parentHeaterHandle ? opts.parentHeaterHandle : null;

    // Auto-select rule: if no heater is selected yet and the caller passed
    // a parentHeaterHandle, set that heater before adding the accessory.
    if (!state.selected_oven_handle && parentHeaterHandle) {
      state.selected_oven_handle = parentHeaterHandle;
      state.selected_oven_variant_id = null;
    }

    // Skip duplicates (idempotent add).
    for (var i = 0; i < state.oven_accessories.length; i++) {
      if (state.oven_accessories[i].handle === handle) {
        // Refresh variantId if the caller supplied one.
        if (variantId !== null) state.oven_accessories[i].variantId = variantId;
        write(state, { kind: 'accessory-updated', payload: { handle: handle, variantId: variantId } });
        return;
      }
    }

    state.oven_accessories.push({ handle: handle, variantId: variantId });
    write(state, { kind: 'accessory-added', payload: { handle: handle, variantId: variantId } });
  }

  function removeAccessory(handle) {
    if (!handle) return;
    var state = read();
    if (!state) return;
    var next = [];
    for (var i = 0; i < state.oven_accessories.length; i++) {
      if (state.oven_accessories[i].handle !== handle) {
        next.push(state.oven_accessories[i]);
      }
    }
    if (next.length === state.oven_accessories.length) return;
    state.oven_accessories = next;
    write(state, { kind: 'accessory-removed', payload: { handle: handle } });
  }

  function clear() {
    removeKey();
    var change = { kind: 'cleared', payload: null };
    notifyLocal(null, change);
    broadcast(change);
  }

  // Subscription -----------------------------------------------------------

  function subscribe(callback) {
    if (typeof callback !== 'function') return function () {};
    subscribers.push(callback);
    return function unsubscribe() {
      var idx = subscribers.indexOf(callback);
      if (idx !== -1) subscribers.splice(idx, 1);
    };
  }

  // Cross-tab listeners: notify local subscribers when another tab writes.
  // - BroadcastChannel: standards-track cross-tab notification (Safari ≥ 15.4).
  // - Storage event: fires in OTHER tabs (not the writer) by spec, so this
  //   doesn't double-fire alongside the in-tab notifyLocal(). Belt-and-
  //   suspenders for contexts where BroadcastChannel doesn't reach.

  function handleExternalUpdate(change) {
    var state = read();
    notifyLocal(state, change || { kind: 'external-update', payload: null });
  }

  if (channel) {
    channel.addEventListener('message', function (e) {
      handleExternalUpdate(e && e.data ? e.data.change : null);
    });
  }

  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    handleExternalUpdate({ kind: 'external-update', payload: null });
  });

  // Public API -------------------------------------------------------------

  window.__hyrrimConfiguratorState = {
    get: read,
    setSauna: setSauna,
    setOven: setOven,
    addAccessory: addAccessory,
    removeAccessory: removeAccessory,
    clear: clear,
    subscribe: subscribe
  };
})();
